import { useEffect, useMemo, useState } from 'react';
import { buildHighlights, hasLiveMaps } from './landingHighlights';

// Module-level cache + in-flight map, like maps/useDistrictGeo.js: the summary
// is static per location for the lifetime of a tab, so returning to the
// landing page doesn't re-fetch it.
const summaryCache = new Map();
const summaryInflight = new Map();

export const clearSummaryCache = () => {
  summaryCache.clear();
  summaryInflight.clear();
};

function fetchCachedSummary(locationId, fetchSummary) {
  if (summaryCache.has(locationId)) return Promise.resolve(summaryCache.get(locationId));
  const pending = summaryInflight.get(locationId);
  if (pending) return pending;

  const request = Promise.resolve(fetchSummary(locationId))
    .then((summary) => {
      summaryCache.set(locationId, summary);
      summaryInflight.delete(locationId);
      return summary;
    })
    .catch((err) => {
      summaryInflight.delete(locationId);
      throw err;
    });
  summaryInflight.set(locationId, request);
  return request;
}

/**
 * "At a glance" tiles for a location's landing page. Returns
 * `{ enabled, loading, highlights, summary }`; `enabled` is false when there is no
 * fetcher (e.g. shared links) or the location has no live maps. Failures
 * resolve to no highlights so the landing page renders as before.
 */
export default function useLocationHighlights(location, fetchSummary) {
  const locationId = location?.id;
  const enabled = Boolean(locationId && fetchSummary && hasLiveMaps(location));

  const [state, setState] = useState(() => ({
    summary: enabled ? summaryCache.get(locationId) || null : null,
    loading: enabled && !summaryCache.has(locationId),
  }));

  useEffect(() => {
    if (!enabled) {
      setState({ summary: null, loading: false });
      return undefined;
    }
    if (summaryCache.has(locationId)) {
      setState({ summary: summaryCache.get(locationId), loading: false });
      return undefined;
    }

    let cancelled = false;
    setState({ summary: null, loading: true });
    fetchCachedSummary(locationId, fetchSummary)
      .then((summary) => {
        if (!cancelled) setState({ summary, loading: false });
      })
      .catch(() => {
        if (!cancelled) setState({ summary: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, locationId, fetchSummary]);

  const highlights = useMemo(
    () => (enabled ? buildHighlights(state.summary, location) : []),
    [enabled, state.summary, location]
  );

  return {
    enabled,
    loading: enabled && state.loading,
    highlights,
    summary: enabled ? state.summary : null,
  };
}
